import React, { useState } from 'react';
import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql, useMutation } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Link, Icon } from '@common/components';
import PropTypes from 'prop-types';
import { Col, Form, Input, Modal, Row, Tag, Typography } from 'antd';
import { humanizeBoolean } from '@common/services';
import { mutationResponseMessage } from 'works/legacy/lib/message-helper';

const { Text } = Typography;

const InfoItem = ({ label, description }) => (
  <>
    <Col span={4}>
      <Text>{label}</Text>
    </Col>
    <Col span={20}>{description}</Col>
  </>
);

export function CandidateAssignmentInfoTable({
  staffingRequestAssignment,
  worksList,
}) {
  const CREATE_EXTERNAL_ID_MUTATION = gql`
    mutation CreateExternalId($input: CreateExternalIdInput!) {
      createExternalId(input: $input) {
        externalId {
          id
        }
        errors {
          ...FullValidationErrorFragment
        }
      }
    }
    ${FullValidationErrorFragment}
  `;

  const DELETE_EXTERNAL_ID_MUTATION = gql`
    mutation DeleteExternalId($id: ID!) {
      deleteExternalId(id: $id) {
        errors {
          ...FullValidationErrorFragment
        }
      }
    }
    ${FullValidationErrorFragment}
  `;

  const currentMembership = useCurrentMembership();
  const [performCreateExternalIdAction] = useMutation(
    CREATE_EXTERNAL_ID_MUTATION,
    {
      refetchQueries: ['CandidateAssignmentQuery'],
    }
  );

  const [performDeleteExternalIdAction] = useMutation(
    DELETE_EXTERNAL_ID_MUTATION,
    {
      refetchQueries: ['CandidateAssignmentQuery'],
    }
  );

  const staffingRequest = staffingRequestAssignment.staffingRequest;
  const canShowTemplateLink = currentMembership.canShowSidebarAdminItem;

  const worksListTemplate =
    worksList?.worksListTemplate || staffingRequest.worksListTemplate;
  const unlinkedWorksListTemplate = !worksList?.worksListTemplate;
  const relinkedWorksListTemplate =
    worksList?.worksListTemplate &&
    worksList.worksListTemplate.id !== staffingRequest.worksListTemplate.id;

  // External ID Stuff
  const [showRemoveExternalIdWarning, setShowRemoveExternalIdWarning] =
    useState(null);
  const [showAddExternalId, setShowAddExternalId] = useState(false);
  const [submittingExternalId, setSubmittingExternalId] = useState(false);
  const canSeeExternalIds = staffingRequestAssignment.canUpdate.value;

  const submitDeleteExternalId = () => {
    if (!showRemoveExternalIdWarning) {
      console.warn(
        'showRemoveExternalId is null but should have an ID',
        showRemoveExternalIdWarning
      );
      return;
    }

    setSubmittingExternalId(true);
    performDeleteExternalIdAction({
      variables: { id: showRemoveExternalIdWarning },
    }).then((response) => {
      mutationResponseMessage({
        gqlResponse: response,
        onSuccess: 'External ID Deleted',
        errorMessage:
          'External ID failed to delete. Please try again or contact your hiring partner.',
      });
      setSubmittingExternalId(false);
      setShowRemoveExternalIdWarning(null);
    });
  };

  const submitCreateExternalId = (values) => {
    setSubmittingExternalId(true);
    performCreateExternalIdAction({
      variables: {
        input: {
          identifier: values.identifier,
          staffingRequestAssignmentId: staffingRequestAssignment.id,
        },
      },
    }).then((response) => {
      mutationResponseMessage({
        gqlResponse: response,
        onSuccess: 'External ID Created',
        errorMessage:
          'External ID failed to create. Please try again or contact your hiring partner.',
      });
      form.resetFields();
      setShowAddExternalId(false);
      setSubmittingExternalId(false);
    });
  };

  const [form] = Form.useForm();

  const addExternalIdModal = (
    <Modal
      className="EditAssignmentExternalIds"
      title="Add External ID"
      visible={showAddExternalId}
      onCancel={() => setShowAddExternalId(false)}
      onOk={form.submit}
      okText="Save"
      okButtonProps={{ disabled: submittingExternalId }}
      width={550}
      centered
    >
      <Form
        form={form}
        className="EditAssignmentModalForm"
        onFinish={(values) => submitCreateExternalId(values)}
      >
        <Form.Item
          label="Identifier"
          name="identifier"
          rules={[
            {
              required: true,
              message: 'Please add an identifier',
            },
          ]}
        >
          <Input size="large" disabled={submittingExternalId} />
        </Form.Item>
      </Form>
    </Modal>
  );

  const removeExternalIdWarningModal = (
    <Modal
      className="RemoveExternalIdWarningModal"
      title="Remove External ID"
      visible={!!showRemoveExternalIdWarning}
      onCancel={() => setShowRemoveExternalIdWarning(null)}
      onOk={submitDeleteExternalId}
      okText="Remove"
      okButtonProps={{ disabled: submittingExternalId }}
      width={550}
      centered
    >
      Remove External ID{' '}
      <strong>
        {showRemoveExternalIdWarning &&
          staffingRequestAssignment.externalIds.find(
            (eid) => eid.id === showRemoveExternalIdWarning
          )?.identifier}
      </strong>
      ?
    </Modal>
  );

  return (
    <>
      {canSeeExternalIds && removeExternalIdWarningModal}
      {canSeeExternalIds && addExternalIdModal}
      <Row gutter={[0, 8]}>
        <InfoItem
          label="Staffing Request ID"
          description={<Text strong>{staffingRequest.externalId || '-'}</Text>}
        />
        <InfoItem
          label="Role"
          description={<Text strong>{staffingRequest.role.name || '-'}</Text>}
        />
        <InfoItem
          label="International Candidate"
          description={
            <Text strong>
              {humanizeBoolean(staffingRequest.international) || '-'}
            </Text>
          }
        />
        <InfoItem
          label="Shift Type"
          description={<Text strong>{staffingRequest.shiftType || '-'}</Text>}
        />
        <InfoItem
          label="Years of Experience"
          description={
            <Text strong>
              {staffingRequest.yearsOfExperienceHumanize || '-'}
            </Text>
          }
        />
        <InfoItem
          label="Specialty"
          description={staffingRequest.staffingRequestSpecialties.map(
            (specialty) => (
              <Row key={specialty.id}>
                <Text strong>
                  {specialty.clinicalSpecialty.displayName}&nbsp;
                </Text>
                <Text type="secondary">({specialty.necessityLevel})</Text>
              </Row>
            )
          )}
        />
        <InfoItem
          label="Technologies"
          description={staffingRequest.staffingRequestTechnologies.map(
            (tech) => (
              <Row key={tech.id}>
                <Text strong>{tech.technology.name}</Text>
              </Row>
            )
          )}
        />
        <InfoItem
          label="Certifications"
          description={staffingRequest.staffingRequestCertifications.map(
            (certification) => (
              <Row key={certification.id}>
                <Text strong>{certification.certification.name}&nbsp;</Text>
                <Text type="secondary">({certification.necessityLevel})</Text>
              </Row>
            )
          )}
        />
        <InfoItem
          label="Description"
          description={<Text strong>{staffingRequest.description || '-'}</Text>}
        />
        {canShowTemplateLink && (
          <InfoItem
            label="Compliance List"
            description={
              <>
                <Link
                  to={`/works/admin/templates/compliance-list/${worksListTemplate.id}/items`}
                >
                  {worksListTemplate.name}
                </Link>
                {unlinkedWorksListTemplate && ' (no longer linked)'}
                {relinkedWorksListTemplate && (
                  <Text type="secondary" delete style={{ marginLeft: 12 }}>
                    {staffingRequest.worksListTemplate.name}
                  </Text>
                )}
              </>
            }
          />
        )}
        {canSeeExternalIds && (
          <InfoItem
            label="External IDs"
            description={
              <>
                {staffingRequestAssignment.externalIds.map((externalId) => (
                  <Tag
                    key={externalId.id}
                    closable
                    onClose={(event) => {
                      event.preventDefault();
                      setShowRemoveExternalIdWarning(externalId.id);
                    }}
                  >
                    {externalId.identifier}
                  </Tag>
                ))}
                <Tag
                  onClick={() => setShowAddExternalId(true)}
                  style={{
                    cursor: 'pointer',
                    border: '1px dashed rgba(0, 0, 0, 0.2)',
                    background: 'transparent',
                  }}
                >
                  <Icon icon="plus" kind="light" size="sm" /> External Id
                </Tag>
              </>
            }
          />
        )}
      </Row>
    </>
  );
}

CandidateAssignmentInfoTable.fragments = {
  staffingRequest: gql`
    fragment CandidateAssignmentInfoTableStaffingRequestFragment on StaffingRequest {
      id
      shiftType
      externalId
      yearsOfExperienceHumanize
      description
      international
      role {
        id
        name
      }
      worksListTemplate {
        id
        name
      }
      staffingRequestCertifications {
        id
        necessityLevel
        certification {
          id
          name
        }
      }
      staffingRequestTechnologies {
        id
        technology {
          id
          name
        }
      }
      staffingRequestSpecialties {
        id
        necessityLevel
        clinicalSpecialty {
          id
          displayName
        }
      }
    }
  `,
};

CandidateAssignmentInfoTable.propTypes = {
  staffingRequestAssignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    externalIds: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        createdAt: PropTypes.string,
        identifier: PropTypes.string,
      })
    ),
    staffingRequest: PropTypes.shape({
      id: PropTypes.string.isRequired,
      shiftType: PropTypes.string.isRequired,
      externalId: PropTypes.string,
      yearsOfExperienceHumanize: PropTypes.string,
      description: PropTypes.string,
      international: PropTypes.bool.isRequired,
      role: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
      worksListTemplate: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
      staffingRequestCertifications: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          necessityLevel: PropTypes.string.isRequired,
          certification: PropTypes.shape({
            name: PropTypes.string.isRequired,
          }),
        })
      ),
      staffingRequestTechnologies: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          technology: PropTypes.shape({
            name: PropTypes.string,
          }),
        })
      ),
      staffingRequestSpecialties: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          necessityLevel: PropTypes.string.isRequired,
          clinicalSpecialty: PropTypes.shape({
            displayName: PropTypes.string.isRequired,
          }),
        })
      ),
    }),
    canUpdate: PropTypes.shape({ value: PropTypes.bool.isRequired }),
  }),
  worksList: PropTypes.shape({
    worksListTemplate: PropTypes.object,
  }),
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
};
